// The onsites opt-in, in the header next to the theme toggle. Owners only.
//
// This button is the one place an owner opts their clinics in or out of
// National Account onsites. Clicking it always opens the billing terms:
// "I agree" opts in (an owner of several clinics then chooses per clinic),
// "Opt Out" opts out. The server records the acceptance in the audit log.

import { useState } from 'react'
import { setOnsiteOpt } from './api.js'
import { useAuth } from './auth.jsx'
import { Button, Dialog, Notice, Pill } from './components/ui/index.js'

const TERMS =
  'National account on-sites are billed at the rates set forth in this portal. ' +
  'On-site or clinic-to-clinic on-site requests are paid at standard interoffice ' +
  'rates, with a minimum of $300 in on-site fees paid to the collecting office.'

export default function OnsitesOpt() {
  const { user, refresh } = useAuth()
  const [step, setStep] = useState(null) // null | 'terms' | 'clinics'
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const clinics = user?.clinics ?? []
  if (!clinics.length) return null

  const allIn = clinics.every((clinic) => clinic.isActive)

  function close() {
    setStep(null)
    setError(null)
  }

  async function apply(targets, optIn) {
    setBusy(true)
    setError(null)
    try {
      for (const clinic of targets) {
        await setOnsiteOpt(clinic.id, optIn)
      }
      await refresh()
      return true
    } catch (caught) {
      setError(caught.message)
      return false
    } finally {
      setBusy(false)
    }
  }

  async function agree() {
    if (clinics.length === 1) {
      if (await apply(clinics, true)) close()
    } else {
      setStep('clinics')
    }
  }

  async function optOut() {
    if (await apply(clinics, false)) close()
  }

  return (
    <>
      <Button variant="ghost" onClick={() => setStep('terms')}>
        {allIn ? 'Opt-out of Onsites' : 'Opt-in for Onsites'}
      </Button>

      {step === 'terms' ? (
        <Dialog
          title="National Account onsites"
          onClose={close}
          footer={
            <>
              <Button variant="ghost" onClick={optOut} disabled={busy}>
                Opt Out
              </Button>
              <span className="spacer" />
              <Button variant="primary" onClick={agree} disabled={busy}>
                I agree
              </Button>
            </>
          }
        >
          <p>{TERMS}</p>
          {error ? <Notice tone="bad">{error}</Notice> : null}
        </Dialog>
      ) : null}

      {step === 'clinics' ? (
        <Dialog
          title="Choose your clinics"
          onClose={close}
          footer={
            <>
              <span className="spacer" />
              <Button variant="primary" onClick={close} disabled={busy}>
                Done
              </Button>
            </>
          }
        >
          <p className="support">
            Opt each clinic in or out. The Owners tab shows the choice to everyone.
          </p>
          <div className="rows">
            {clinics.map((clinic) => (
              <div key={clinic.id} className="row">
                <div className="row-main">
                  <div className="row-primary">{clinic.name}</div>
                  <div className="row-meta">
                    <Pill tone={clinic.isActive ? 'ok' : 'neutral'}>
                      {clinic.isActive ? 'Opted in' : 'Opted out'}
                    </Pill>
                  </div>
                </div>
                <div className="row-trail">
                  <Button disabled={busy} onClick={() => apply([clinic], !clinic.isActive)}>
                    {clinic.isActive ? 'Opt out' : 'Opt in'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
          {error ? <Notice tone="bad">{error}</Notice> : null}
        </Dialog>
      ) : null}
    </>
  )
}
